import AddIcon from "@mui/icons-material/Add";
import {
  Button,
  IconButton,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import LeafConditionRow from "./LeafConditionRow";
import RemoveIconButton from "./RemoveIconButton";
import {
  newGroupCondition,
  newLeafCondition,
  removeByKey,
  updateByKey,
  type Combinator,
  type ConditionNode,
  type WizardParameter,
} from "./clearanceRulesConfig";
import type { LidarType } from "../schemas";
import t from "../translations";

type Props = {
  nodes: ConditionNode[];
  combinator: Combinator;
  parameters: WizardParameter[];
  lidarTypes: LidarType[];
  onChangeNodes: (nodes: ConditionNode[]) => void;
  onChangeCombinator: (combinator: Combinator) => void;
  // The rule's own top-level condition list keeps at least one row - a
  // nested group can be removed down to nothing, which just removes the
  // group itself from its parent.
  requireAtLeastOne?: boolean;
};

export default function ConditionListEditor({
  nodes,
  combinator,
  parameters,
  lidarTypes,
  onChangeNodes,
  onChangeCombinator,
  requireAtLeastOne,
}: Props) {
  const updateNode = (key: string, updated: ConditionNode) =>
    onChangeNodes(updateByKey(nodes, key, updated));

  const removeNode = (key: string) => onChangeNodes(removeByKey(nodes, key));

  return (
    <Stack spacing={1}>
      {nodes.map((node) =>
        node.kind === "leaf" ? (
          <LeafConditionRow
            key={node.key}
            condition={node}
            parameters={parameters}
            lidarTypes={lidarTypes}
            onChange={(updated) => updateNode(node.key, updated)}
            onRemove={() => removeNode(node.key)}
            disableRemove={requireAtLeastOne && nodes.length === 1}
          />
        ) : (
          <Paper
            key={node.key}
            variant="outlined"
            sx={{ p: 1.5, borderLeft: 3, borderLeftColor: "primary.light" }}
          >
            <Stack direction="row" sx={{ justifyContent: "flex-end" }}>
              <RemoveIconButton
                aria-label={t.RemoveConditionButton}
                onClick={() => removeNode(node.key)}
              />
            </Stack>
            <ConditionListEditor
              nodes={node.children}
              combinator={node.combinator}
              parameters={parameters}
              lidarTypes={lidarTypes}
              onChangeNodes={(children) => updateNode(node.key, { ...node, children })}
              onChangeCombinator={(nextCombinator) =>
                updateNode(node.key, { ...node, combinator: nextCombinator })
              }
            />
          </Paper>
        )
      )}
      <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
        <IconButton
          size="small"
          aria-label={t.AddConditionButton}
          onClick={() => onChangeNodes([...nodes, newLeafCondition()])}
        >
          <AddIcon fontSize="small" />
        </IconButton>
        <Button
          size="small"
          onClick={() => onChangeNodes([...nodes, newGroupCondition()])}
        >
          {t.AddGroupButton}
        </Button>
        {nodes.length > 1 && (
          <ToggleButtonGroup
            size="small"
            exclusive
            value={combinator}
            onChange={(_event, value: Combinator | null) =>
              value && onChangeCombinator(value)
            }
          >
            <ToggleButton value="all">{t.CombinatorAllLabel}</ToggleButton>
            <ToggleButton value="any">{t.CombinatorAnyLabel}</ToggleButton>
          </ToggleButtonGroup>
        )}
      </Stack>
    </Stack>
  );
}
